import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import OrderStatusBadge from "@/features/order-status/OrderStatusBadge";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { AppLineCardAction } from "@/features/line-sheet-sets/app-flat-card";
import { GridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import { LineSheetOrderSheetItem } from "@/features/line-sheet-sets/detail/hooks/use-line-sheet-order-sheet-items";
import { StoredObject } from "@/features/types";

// 라인시트 카드를 펼쳤을 때 카드 안에 들어가는 오더시트 한 줄
export interface LineSheetOrderSheetRow extends GridRow, LineSheetOrderSheetItem {
  // 부모 라인시트가 파싱 실패(파일만 있는) 라인시트면 상품 단위 오더시트 화면이 없다.
  isFallbackLineSheet: boolean;
  // 오더시트에 내보낸 파일이 아직 없을 때 대신 내려받을 부모 라인시트 파일(오더시트 세트 화면과 같은 규칙)
  lineSheetFile: Pick<StoredObject, "id" | "name"> | null;
  lineSheetUnoFormFile: Pick<StoredObject, "id" | "name"> | null;
}

// 주문 값은 초록, 확정 값은 파랑 — 라인시트 줄의 합계 칸도 같은 색을 쓴다
export const ORDERED_COLOR = "#008040";
export const CONFIRMED_COLOR = "#1272EF";
const LINE_COLOR = "#EFEFED";

const cellProps = {
  padding: "10px 8px",
  fontSize: "12px",
  borderBottom: `0.5px solid ${LINE_COLOR}`,
} as const;

const headerCellProps = {
  ...cellProps,
  color: "#000",
  fontWeight: "700",
  textTransform: "none",
  letterSpacing: "normal",
  lineHeight: "16px",
} as const;

interface LineSheetOrderSheetTableProps {
  rows: LineSheetOrderSheetRow[];
  formatAmount: (amount: number) => string;
  // 오더시트 줄마다 붙는 다운로드(발행사 양식 / 표준화 양식)
  getActions: (row: LineSheetOrderSheetRow) => AppLineCardAction[];
  onOpen: (row: LineSheetOrderSheetRow) => void;
  // (부티크) 이 라인시트에 들어온 모든 주문을 상품별로 합쳐 보는 화면으로. 없으면 링크를 감춘다.
  onViewAllOrderedProducts?: () => void;
}

/**
 * 라인시트 카드를 펼치면 카드 아래쪽이 길어지며 들어가는 오더시트 표.
 * 한 줄 = 오더시트 하나(바이어 · 주문번호 · 주문/확정 수량 · 주문/확정 금액 · 상태 · 상세 보기).
 */
export default function LineSheetOrderSheetTable({
  rows,
  formatAmount,
  getActions,
  onOpen,
  onViewAllOrderedProducts,
}: LineSheetOrderSheetTableProps) {
  const { t, tTitle, tLocalDateString } = useI18nHelper();
  const quantityText = (quantity: number) =>
    `${quantity.toLocaleString()}${t("quantity")}`;

  return (
    <Box overflowX={"auto"}>
      {onViewAllOrderedProducts && (
        <Flex justifyContent={"flex-end"} padding={"4px 8px 0"}>
          <Text
            as={"button"}
            type={"button"}
            fontSize={"12px"}
            color={CONFIRMED_COLOR}
            _hover={{ textDecoration: "underline" }}
            onClick={(event) => {
              event.stopPropagation();
              onViewAllOrderedProducts();
            }}
          >
            {t("all_ordered_products.view_line_sheet")}
          </Text>
        </Flex>
      )}
      <Table variant={"unstyled"} width={"100%"}>
        <Thead>
          <Tr>
            <Th {...headerCellProps}>
              <Text>{tTitle("buyer")}</Text>
              <Text>{tTitle("order_no")}</Text>
            </Th>
            <Th {...headerCellProps} textAlign={"end"} width={"96px"}>
              <Text>{tTitle("ordered_quantity")}</Text>
              <Text>{tTitle("confirmed_quantity")}</Text>
            </Th>
            <Th {...headerCellProps} textAlign={"end"} width={"120px"}>
              <Text>{tTitle("ordered_amount")}</Text>
              <Text>{tTitle("confirmed_amount")}</Text>
            </Th>
            <Th {...headerCellProps} textAlign={"center"} width={"150px"}>
              {tTitle("status")}
            </Th>
            <Th {...headerCellProps} textAlign={"end"} width={"120px"}>
              {tTitle("view_details")}
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => {
            const { orderSheetSet: set, orderSheet } = row;
            const isCanceled = orderSheet.status === "CANCELED";
            const isConfirmed =
              !!orderSheet.confirmedQuantity || !!orderSheet.confirmedAmount;
            const canceledProps = isCanceled
              ? { color: "#8F8F8C", textDecoration: "line-through" }
              : {};
            const actions = getActions(row);
            return (
              <Tr
                key={row._rowId}
                cursor={"pointer"}
                _hover={{ background: "#F7F9FA" }}
                onClick={() => onOpen(row)}
              >
                <Td {...cellProps}>
                  <Text fontWeight={"500"} {...canceledProps}>
                    {set.createdByCompany?.name ?? "-"}
                  </Text>
                  <Text fontSize={"10px"} color={"#8F8F8C"}>
                    {`#${set.id}`}
                    {set.submittedAt &&
                      ` · ${tLocalDateString(new Date(set.submittedAt))}`}
                  </Text>
                </Td>
                <Td {...cellProps} textAlign={"end"} whiteSpace={"nowrap"}>
                  <Text color={ORDERED_COLOR} {...canceledProps}>
                    {quantityText(orderSheet.orderedQuantity)}
                  </Text>
                  <Text color={CONFIRMED_COLOR} {...canceledProps}>
                    {isConfirmed
                      ? quantityText(orderSheet.confirmedQuantity)
                      : t("unconfirmed")}
                  </Text>
                </Td>
                <Td {...cellProps} textAlign={"end"} whiteSpace={"nowrap"}>
                  <Text color={ORDERED_COLOR} {...canceledProps}>
                    {formatAmount(orderSheet.orderedAmount)}
                  </Text>
                  <Text color={CONFIRMED_COLOR} {...canceledProps}>
                    {isConfirmed
                      ? formatAmount(orderSheet.confirmedAmount)
                      : t("unconfirmed")}
                  </Text>
                </Td>
                <Td {...cellProps}>
                  <Flex justifyContent={"center"}>
                    <OrderStatusBadge status={orderSheet.status} />
                  </Flex>
                </Td>
                <Td {...cellProps}>
                  <Flex
                    justifyContent={"flex-end"}
                    alignItems={"center"}
                    gap={"4px"}
                  >
                    <Flex alignItems={"center"} color={"#444440"}>
                      <Text whiteSpace={"nowrap"}>{t("order_detail")}</Text>
                      <ChevronRightIcon />
                    </Flex>
                    {actions.length > 0 && (
                      // 메뉴를 눌러도 줄 전체의 '주문 상세 열기'가 같이 눌리지 않게 막는다.
                      <Box onClick={(e) => e.stopPropagation()}>
                        <Menu placement={"bottom-end"} isLazy>
                          <MenuButton
                            as={IconButton}
                            aria-label={"download"}
                            icon={<Text lineHeight={"1"}>⋯</Text>}
                            size={"xs"}
                            variant={"ghost"}
                          />
                          <MenuList fontSize={"12px"} minWidth={"160px"}>
                            {actions.map((action) => (
                              <MenuItem
                                key={action.name}
                                isDisabled={action.isDisabled || action.isLoading}
                                // 누를 수 없는 이유(예: 확정 수량 없음)를 마우스를 올리면 보여준다
                                title={
                                  action.isDisabled
                                    ? action.disabledMessage
                                    : undefined
                                }
                                onClick={action.onClick}
                              >
                                {action.name}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      </Box>
                    )}
                  </Flex>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}
